/**
 * PartnerCapture — Vollbild-Erfassung für Provisionspartner.
 *
 * Gleiches Look & Feel wie der Case Editor: Bottom-Navigation mit dem
 * großen runden Foto-Button in der Mitte (öffnet die Multi-Kategorie-
 * Kamera). Steps: Kunde → Fahrzeug → Fotos → Unterschrift → Notiz → Senden.
 * Alle Eingaben werden automatisch als Entwurf gespeichert.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  User, Car, Camera, PenLine, FileText, CheckCircle2, ArrowLeft,
  Loader2, ImagePlus, X, Send, Circle, Shield,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import CameraCapture from "@/components/CameraCapture";
import SignatureCanvas from "@/components/SignatureCanvas";
import InsurerCombobox from "@/components/InsurerCombobox";
import { PHOTO_GUIDE } from "@/components/PhotoGuide";

const ACCIDENT_TYPES = [
  "Auffahrunfall",
  "Kettenauffahrunfall",
  "Parkschaden / Parkrempler",
  "Spurwechsel-Kollision",
  "Vorfahrt missachtet",
  "Abbiegeunfall",
  "Rückwärtsfahren / Ausparken",
  "Wendemanöver",
  "Unfall im Kreisverkehr",
  "Fahrerflucht (Gegner unbekannt)",
  "Wildunfall",
  "Hagel- / Unwetterschaden",
  "Steinschlag",
  "Vandalismus",
  "Sonstige",
];

const STEPS = [
  { key: "customer", label: "Kunde", icon: User, testid: "capture-nav-customer" },
  { key: "insurance", label: "Vers.", icon: Shield, testid: "capture-nav-insurance" },
  { key: "vehicle", label: "Fzg.", icon: Car, testid: "capture-nav-vehicle" },
  { key: "photos", label: "Fotos", icon: Camera, testid: "capture-nav-photos", featured: true },
  { key: "signature", label: "Sig.", icon: PenLine, testid: "capture-nav-signature" },
  { key: "note", label: "Notiz", icon: FileText, testid: "capture-nav-note" },
  { key: "send", label: "Senden", icon: CheckCircle2, testid: "capture-nav-send" },
];

function CaptureNav({ current, onChange, completeness, onCameraClick }) {
  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 bg-white/90 backdrop-blur-xl border-t border-zinc-200 safe-bottom"
      data-testid="capture-bottom-nav"
    >
      <div className="grid grid-cols-7 items-end px-1 pt-1 pb-1 max-w-3xl mx-auto">
        {STEPS.map((s) => {
          const Icon = s.icon;
          const isActive = current === s.key;
          const done = completeness[s.key];
          if (s.featured) {
            return (
              <div key={s.key} className="flex justify-center relative">
                <button
                  onClick={onCameraClick}
                  data-testid={s.testid}
                  aria-label="Fotos"
                  className={cn(
                    "relative -mt-10 h-14 w-14 rounded-full brand-gradient-bg text-white",
                    "flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(18,169,232,0.55)]",
                    "border-4 border-white transition-all active:scale-95",
                    isActive && "ring-2 ring-[#12A9E8]/60",
                    !isActive && done && "ring-[3px] ring-blue-400 shadow-[0_8px_24px_-6px_rgba(59,130,246,0.55)]"
                  )}
                >
                  <Icon className="h-6 w-6" strokeWidth={1.9} />
                </button>
              </div>
            );
          }
          return (
            <button
              key={s.key}
              onClick={() => onChange(s.key)}
              data-testid={s.testid}
              aria-label={s.label}
              className={cn(
                "flex flex-col items-center justify-center gap-0.5 py-1 rounded-lg transition",
                isActive ? "text-[#12A9E8]" : done ? "text-blue-700 bg-blue-50" : "text-zinc-500 hover:text-zinc-900"
              )}
            >
              <div
                className={cn(
                  "h-7 w-7 rounded-md flex items-center justify-center transition",
                  isActive ? "bg-[#12A9E8]/10 ring-1 ring-[#12A9E8]/30" : done ? "bg-blue-100 ring-1 ring-blue-200" : "bg-transparent"
                )}
              >
                <Icon className="h-[16px] w-[16px]" strokeWidth={done || isActive ? 2 : 1.8} />
              </div>
              <span className={cn("text-[9px] font-semibold leading-none truncate max-w-full px-0.5", isActive ? "text-[#12A9E8]" : done ? "text-blue-700" : "font-medium")}>
                {s.label}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

function StepHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <div className="h-10 w-10 rounded-xl bg-cyan-50 text-cyan-700 flex items-center justify-center shrink-0">
        <Icon className="h-5 w-5" strokeWidth={1.9} />
      </div>
      <div>
        <div className="font-display font-bold text-lg leading-tight">{title}</div>
        {subtitle && <div className="text-xs text-zinc-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

export default function PartnerCapture() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState("customer");
  const [cameraOpen, setCameraOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const dirtyRef = useRef({});
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);

  const backend = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const photoUrl = (p) => `${backend}/api/files/download/${encodeURI(p.storage_path)}?auth=${encodeURIComponent(accessToken || "")}`;

  useEffect(() => {
    api.get(`/partner-orders/${orderId}`)
      .then(({ data }) => setOrder(data.order))
      .catch((e) => { toast.error(apiError(e)); navigate("/provision"); })
      .finally(() => setLoading(false));
  }, [orderId, navigate]);

  const canEdit = order && !order.case_id && !["report_done", "paid"].includes(order.status);

  const flushSave = useCallback(async () => {
    const fields = { ...dirtyRef.current };
    if (Object.keys(fields).length === 0) return;
    dirtyRef.current = {};
    setSaving(true);
    try {
      await api.patch(`/partner-orders/${orderId}/details`, fields);
    } catch (e) {
      toast.error(apiError(e, "Speichern fehlgeschlagen"));
    } finally {
      setSaving(false);
    }
  }, [orderId]);

  const setField = (k) => (e) => {
    const v = e.target.value;
    setOrder((o) => ({ ...o, [k]: v }));
    dirtyRef.current[k] = v;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushSave, 800);
  };

  const goStep = (key) => { flushSave(); setStep(key); };
  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const uploadGallery = async (list) => {
    const imgs = Array.from(list || []).filter((f) => f.type.startsWith("image/"));
    if (imgs.length === 0) return;
    setUploading(true);
    try {
      const fd = new FormData();
      imgs.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/partner-orders/${orderId}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setOrder((o) => ({ ...o, photos: [...(o.photos || []), ...(data.photos || [])] }));
      toast.success(`${(data.photos || []).length} Foto(s) hochgeladen`);
    } catch (e) {
      toast.error(apiError(e, "Upload fehlgeschlagen"));
    } finally {
      setUploading(false);
    }
  };

  const deletePhoto = async (photoId) => {
    try {
      await api.delete(`/partner-orders/${orderId}/photos/${photoId}`);
      setOrder((o) => ({ ...o, photos: (o.photos || []).filter((p) => p.id !== photoId) }));
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const submit = async () => {
    await flushSave();
    if (!(order.customer_name || "").trim()) { toast.error("Bitte den Namen des Kunden angeben."); setStep("customer"); return; }
    if ((order.photos || []).length === 0) { toast.error("Bitte mindestens ein Foto hochladen."); setStep("photos"); return; }
    setSubmitting(true);
    try {
      await api.post(`/partner-orders/${orderId}/submit`);
      toast.success("Auftrag eingereicht! 🎉", {
        description: "Unser Team übernimmt den Rest — du siehst den Status in deiner Liste.",
      });
      navigate("/provision");
    } catch (e) {
      toast.error(apiError(e, "Einreichen fehlgeschlagen"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-50">
        <Loader2 className="h-7 w-7 animate-spin text-zinc-300" />
      </div>
    );
  }

  const photos = order.photos || [];
  const completeness = {
    customer: !!(order.customer_name || "").trim(),
    insurance: !!(order.insurance_name || "").trim() || !!(order.opponent_plate || "").trim(),
    vehicle: !!(order.license_plate || "").trim() || !!(order.vehicle_make || "").trim(),
    photos: photos.length > 0,
    signature: !!order.signature,
    note: !!(order.note || "").trim() || !!(order.accident_type || "").trim(),
    send: order.status !== "draft",
  };

  const field = (label, key, props = {}) => (
    <div>
      <Label className="text-xs">{label}</Label>
      <Input value={order[key] || ""} onChange={setField(key)} disabled={!canEdit} className="mt-1" data-testid={`capture-field-${key}`} {...props} />
    </div>
  );

  const renderStep = () => {
    switch (step) {
      case "customer":
        return (
          <div className="space-y-4" data-testid="capture-step-customer">
            <StepHeader icon={User} title="Kunde" subtitle="Wer ist der Geschädigte?" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field("Vorname", "customer_firstname")}
              {field("Nachname *", "customer_name")}
              {field("Telefon", "phone", { type: "tel" })}
              {field("E-Mail", "email", { type: "email" })}
              {field("Ort", "city")}
              {field("Unfalldatum", "accident_date", { type: "date" })}
            </div>
            <Button className="w-full sm:w-auto" onClick={() => goStep("insurance")} data-testid="capture-next-insurance">
              Weiter: Versicherung
            </Button>
          </div>
        );
      case "insurance":
        return (
          <div className="space-y-4" data-testid="capture-step-insurance">
            <StepHeader icon={Shield} title="Versicherung" subtitle="Gegnerische Versicherung — falls bekannt, einfach auswählen" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl">
              <div>
                <Label className="text-xs">Gegnerische Versicherung</Label>
                <div className="mt-1">
                  <InsurerCombobox
                    value={order.insurance_name || ""}
                    onChange={(name) => {
                      setOrder((o) => ({ ...o, insurance_name: name }));
                      dirtyRef.current.insurance_name = name;
                      if (timerRef.current) clearTimeout(timerRef.current);
                      timerRef.current = setTimeout(flushSave, 800);
                    }}
                    testid="capture-field-insurance"
                    disabled={!canEdit}
                  />
                </div>
              </div>
              {field("Kennzeichen des Gegners", "opponent_plate", { placeholder: "M-GG 123" })}
            </div>
            <div className="text-xs text-zinc-400">
              Nicht sicher? Einfach leer lassen — das Team ermittelt die Versicherung über den Zentralruf.
            </div>
            <Button className="w-full sm:w-auto" onClick={() => goStep("vehicle")} data-testid="capture-next-vehicle">
              Weiter: Fahrzeug
            </Button>
          </div>
        );
      case "vehicle":
        return (
          <div className="space-y-4" data-testid="capture-step-vehicle">
            <StepHeader icon={Car} title="Fahrzeug" subtitle="Angaben zum beschädigten Fahrzeug" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {field("Kennzeichen", "license_plate", { placeholder: "B-XX 1234" })}
              {field("Marke", "vehicle_make", { placeholder: "z. B. BMW" })}
              {field("Modell", "vehicle_model", { placeholder: "z. B. 320d" })}
            </div>
            <div className="text-xs text-zinc-400">
              Tipp: Fahrzeugschein einfach im Foto-Schritt mitfotografieren — dann brauchst du hier nichts weiter.
            </div>
            <Button className="w-full sm:w-auto" onClick={() => { goStep("photos"); }} data-testid="capture-next-photos">
              Weiter: Fotos
            </Button>
          </div>
        );
      case "photos":
        return (
          <div className="space-y-4" data-testid="capture-step-photos">
            <StepHeader icon={Camera} title="Fotos" subtitle="Schadenfotos + Fahrzeugschein — mind. 1 Foto ist Pflicht" />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => setCameraOpen(true)}
                disabled={!canEdit}
                className="brand-gradient-bg text-white rounded-xl p-4 flex items-center justify-center gap-2 font-semibold shadow-md active:scale-95 transition disabled:opacity-50"
                data-testid="capture-open-camera-btn"
              >
                <Camera className="h-5 w-5" strokeWidth={1.9} /> Fotos aufnehmen
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={!canEdit || uploading}
                className="rounded-xl border-2 border-dashed border-zinc-300 bg-zinc-50 hover:border-cyan-400 hover:bg-cyan-50/40 transition p-4 flex items-center justify-center gap-2 text-zinc-600 font-semibold disabled:opacity-50"
                data-testid="capture-gallery-btn"
              >
                {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
                Aus Galerie hochladen
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { uploadGallery(e.target.files); e.target.value = ""; }}
              data-testid="capture-gallery-input"
            />
            {photos.length === 0 ? (
              <div className="rounded-xl border border-zinc-200 bg-white p-8 text-center text-sm text-zinc-400" data-testid="capture-photos-empty">
                Noch keine Fotos — tippe auf den Kamera-Button.
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="capture-photos-grid">
                {photos.map((p) => (
                  <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden border border-zinc-200 bg-zinc-100">
                    <img src={photoUrl(p)} alt="" className="w-full h-full object-cover" loading="lazy" />
                    {canEdit && (
                      <button
                        type="button"
                        onClick={() => deletePhoto(p.id)}
                        className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                        data-testid={`capture-photo-delete-${p.id}`}
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <Button className="w-full sm:w-auto" onClick={() => goStep("signature")} data-testid="capture-next-signature">
              Weiter: Unterschrift
            </Button>
          </div>
        );
      case "signature":
        return (
          <div className="space-y-4" data-testid="capture-step-signature">
            <StepHeader icon={PenLine} title="Unterschrift des Kunden" subtitle="Abtretungserklärung — der Kunde unterschreibt direkt auf deinem Handy" />
            <div className="rounded-xl border border-zinc-200 bg-zinc-50/70 px-4 py-3 text-xs text-zinc-600 leading-relaxed">
              Der Geschädigte tritt seine Ansprüche auf Erstattung der Gutachterkosten an
              die gegnerische Versicherung ab. Das Team ergänzt die vollständige
              Abtretungserklärung im Gutachten.
            </div>
            <SignatureCanvas
              caseId={orderId}
              uploadPath={`/partner-orders/${orderId}/signature`}
              existing={order.signature}
              defaultSignerName={[order.customer_firstname, order.customer_name].filter(Boolean).join(" ").trim()}
              onChange={(sig) => setOrder((o) => ({ ...o, signature: sig }))}
            />
            <Button className="w-full sm:w-auto" onClick={() => goStep("note")} data-testid="capture-next-note">
              Weiter: Notiz
            </Button>
          </div>
        );
      case "note":
        return (
          <div className="space-y-4" data-testid="capture-step-note">
            <StepHeader icon={FileText} title="Notiz" subtitle="Was ist passiert? Alles, was das Team wissen sollte." />
            <div className="max-w-md">
              <Label className="text-xs">Unfallart</Label>
              <Select
                value={order.accident_type || ""}
                onValueChange={(v) => {
                  setOrder((o) => ({ ...o, accident_type: v }));
                  dirtyRef.current.accident_type = v;
                  flushSave();
                }}
                disabled={!canEdit}
              >
                <SelectTrigger className="mt-1" data-testid="capture-field-accident-type">
                  <SelectValue placeholder="Unfallart auswählen…" />
                </SelectTrigger>
                <SelectContent className="max-h-64">
                  {ACCIDENT_TYPES.map((t) => (
                    <SelectItem key={t} value={t} data-testid={`capture-accident-type-option-${t}`}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea
              rows={6}
              value={order.note || ""}
              onChange={setField("note")}
              disabled={!canEdit}
              placeholder="z. B. Auffahrunfall an der Ampel, Heckschaden, Gegner hat Schuld anerkannt…"
              data-testid="capture-field-note"
            />
            <Button className="w-full sm:w-auto" onClick={() => goStep("send")} data-testid="capture-next-send">
              Weiter: Einreichen
            </Button>
          </div>
        );
      case "send": {
        const rows = [
          { label: "Kunde", ok: completeness.customer, required: true, detail: [order.customer_firstname, order.customer_name].filter(Boolean).join(" ") || "—" },
          { label: "Versicherung", ok: completeness.insurance, detail: [order.insurance_name, order.opponent_plate ? `Gegner: ${order.opponent_plate}` : ""].filter(Boolean).join(" · ") || "—" },
          { label: "Fahrzeug", ok: completeness.vehicle, detail: [order.license_plate, order.vehicle_make, order.vehicle_model].filter(Boolean).join(" · ") || "—" },
          { label: "Fotos", ok: completeness.photos, required: true, detail: `${photos.length} Foto(s)` },
          { label: "Unterschrift", ok: completeness.signature, detail: order.signature ? `Unterschrieben${order.signature.signer_name ? ` von ${order.signature.signer_name}` : ""}` : "—" },
          { label: "Notiz", ok: completeness.note, detail: [order.accident_type, order.note ? `${order.note.slice(0, 50)}${order.note.length > 50 ? "…" : ""}` : ""].filter(Boolean).join(" · ") || "—" },
        ];
        return (
          <div className="space-y-4" data-testid="capture-step-send">
            <StepHeader icon={Send} title="Einreichen" subtitle="Prüfe kurz — dann ab zum Team." />
            <div className="rounded-2xl border border-zinc-200 bg-white divide-y divide-zinc-50">
              {rows.map((r) => (
                <div key={r.label} className="px-4 py-3 flex items-center gap-3" data-testid={`capture-summary-${r.label.toLowerCase()}`}>
                  {r.ok ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" />
                  ) : (
                    <Circle className={cn("h-5 w-5 shrink-0", r.required ? "text-red-400" : "text-zinc-300")} />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">
                      {r.label}{r.required && <span className="text-red-500"> *</span>}
                    </div>
                    <div className="text-xs text-zinc-400 truncate">{r.detail}</div>
                  </div>
                </div>
              ))}
            </div>
            {order.status === "draft" ? (
              <Button
                size="lg"
                className="w-full brand-gradient-bg text-white"
                onClick={submit}
                disabled={submitting}
                data-testid="capture-submit-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Auftrag einreichen — {new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(order.commission_amount || 200)} Provision
              </Button>
            ) : (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 font-semibold flex items-center gap-2" data-testid="capture-already-submitted">
                <CheckCircle2 className="h-4 w-4" /> Auftrag ist eingereicht — das Team kümmert sich.
                {canEdit && <span className="font-normal text-emerald-600">Du kannst Fotos & Daten noch ergänzen, bis das Team übernimmt.</span>}
              </div>
            )}
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50" data-testid="partner-capture-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-zinc-200">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button
            onClick={() => { flushSave(); navigate("/provision"); }}
            className="h-9 w-9 rounded-lg hover:bg-zinc-100 flex items-center justify-center text-zinc-600"
            data-testid="capture-back-btn"
            aria-label="Zurück"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-sm truncate">
              {order.status === "draft" ? "Auftrag erfassen" : "Auftrag ansehen"}
            </div>
            <div className="text-[11px] text-zinc-400 flex items-center gap-1.5">
              {saving ? (
                <><Loader2 className="h-3 w-3 animate-spin" /> Speichert…</>
              ) : (
                <>{order.status === "draft" ? "Entwurf — wird automatisch gespeichert" : "Eingereicht"}</>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-3xl mx-auto px-4 py-6 pb-32">{renderStep()}</main>

      {/* Bottom Navigation */}
      <CaptureNav
        current={step}
        onChange={goStep}
        completeness={completeness}
        onCameraClick={() => { flushSave(); setStep("photos"); if (canEdit) setCameraOpen(true); }}
      />

      {/* Kamera (gleiche wie in der Webapp) */}
      <CameraCapture
        open={cameraOpen}
        onClose={() => setCameraOpen(false)}
        caseId={orderId}
        categories={PHOTO_GUIDE}
        uploadPath={`/partner-orders/${orderId}/photos`}
        onUploaded={(ps) => setOrder((o) => ({ ...o, photos: [...(o.photos || []), ...ps] }))}
      />
    </div>
  );
}
